import { TranslationFunction } from '@/src/types/translations'
import { Box, Typography } from '@mui/material'
import { type PassengerType, type ProcessedBaggageData } from './useBaggageData'

interface BaggageTableProps {
    processedData: ProcessedBaggageData
    t: TranslationFunction<'common'>
}

// Style constants
const STYLES = {
    tableContainer: {
        mt: 2
    },
    table: {
        border: '1px solid',
        borderColor: 'borders.default',
        borderRadius: 1,
        overflow: 'hidden'
    },
    headerRow: {
        display: 'flex',
        bgcolor: 'background.default'
    },
    dataRow: {
        display: 'flex',
        borderTop: '1px solid',
        borderColor: 'borders.default'
    },
    cell: {
        flex: 1,
        p: 1.5
    },
    headerText: {
        fontSize: '14px',
        color: 'textColors.subheading',
        textAlign: 'center' as const
    },
    labelText: {
        fontSize: '14px',
        fontWeight: 500,
        textAlign: 'center' as const
    },
    dataText: {
        fontSize: '14px',
        fontWeight: 600,
        textAlign: 'center' as const
    }
} as const

/**
 * BaggageTable - Pure Presentational Component
 *
 * Displays baggage allowances for different passenger types in a clean table format.
 * Receives all processed data via props - no business logic or state management.
 */
const BaggageTable: React.FC<BaggageTableProps> = ({ processedData, t }) => {
    // Constants
    const PASSENGER_LABELS: Record<PassengerType, string> = {
        ADT: t('Adult')
    } as const
    // Render functions for cleaner JSX
    const renderHeaderCell = (text: string, isFirst = false) => (
        <Box sx={{ ...STYLES.cell, backgroundColor: 'neutral.100' }}>
            <Typography sx={isFirst ? { ...STYLES.headerText, textAlign: 'left' } : STYLES.headerText}>
                {text}
            </Typography>
        </Box>
    )

    const renderDataCell = (text: string, isLabel = false) => (
        <Box sx={STYLES.cell}>
            <Typography sx={isLabel ? STYLES.labelText : STYLES.dataText}>{text}</Typography>
        </Box>
    )

    const renderPassengerRow = (paxType: PassengerType) => {
        const checkinValue = processedData.checkinData[paxType]
        const cabinValue = processedData.cabinData[paxType]

        return (
            <Box sx={STYLES.dataRow}>
                {renderDataCell(PASSENGER_LABELS[paxType], true)}
                {renderDataCell(checkinValue)}
                {renderDataCell(cabinValue)}
            </Box>
        )
    }

    return (
        <Box sx={STYLES.tableContainer}>
            <Box sx={STYLES.table}>
                {/* Header Row */}
                <Box sx={STYLES.headerRow}>
                    {renderHeaderCell('', true)}
                    {renderHeaderCell(t('checkin'))}
                    {renderHeaderCell(t('cabinBaggage'))}
                </Box>
                {renderPassengerRow('ADT')}
            </Box>
        </Box>
    )
}

export default BaggageTable
